import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { settingsAPI, usersAPI } from '../services/api';
import {
  FiSun, FiMoon, FiShield, FiBell, FiZap, FiGlobe, FiLock,
  FiLogOut, FiCheck, FiDollarSign, FiClock
} from 'react-icons/fi';
import './Settings.css';

function SignatureUpload() {
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    usersAPI.me().then((r) => setCurrent(r.data?.signature_file || null)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true); setMsg('');
    try {
      const r = await usersAPI.uploadSignature(file);
      setCurrent(r.data?.signature_file || null);
      setMsg('Signature saved.');
    } catch (err) {
      setMsg('Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {current && (
        <img
          src={current}
          alt="Your signature"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{ maxHeight: 60, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 6, display: 'block', marginBottom: 8 }}
        />
      )}
      {current && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>A signature is on file.</div>}
      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} disabled={busy} />
      {msg && <div style={{ fontSize: 12, color: '#166534', marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

const TABS = [
  { id: 'general', label: 'General & UI', icon: FiSun },
  // Every other tab is org-wide config (2FA policy, session kill switch,
  // notification routing, SLA timers, webhook/API-key secrets) — admin
  // only, matching the backend's admin-gated GET (see fetchSettings above).
  { id: 'security', label: 'Security', icon: FiShield, adminOnly: true },
  { id: 'notifications', label: 'Notifications', icon: FiBell, adminOnly: true },
  { id: 'workflow', label: 'Workflow', icon: FiZap, adminOnly: true },
  { id: 'integrations', label: 'Integrations', icon: FiGlobe, adminOnly: true },
];

const Settings = () => {
  const { user, logout, isAdmin, isAgent } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // GET /api/settings/ is admin-only server-side now (it returns org-wide
    // secrets like webhook_url/whatsapp_api_key, not personal preferences —
    // see config/views.py) — skip the call entirely for non-admins rather
    // than let it 403 in the console; they only ever see the Account/Theme
    // section below, which needs none of this.
    if (isAdmin()) {
      fetchSettings();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await settingsAPI.get();
      setSettings(data);
      setLocal(data || {});
    } catch {
      setSettings({});
      setLocal({});
    } finally {
      setLoading(false);
    }
  };

  const updateLocal = (key, value) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await settingsAPI.update(local);
      setSettings(data);
      setLocal(data);
      if (local.theme !== theme) toggleTheme(local.theme);
      setMessage({ type: 'success', text: 'Settings saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleKillSessions = async (keepMe = true) => {
    if (!window.confirm(`Revoke all sessions${keepMe ? ' (you stay logged in)' : ''}?`)) return;
    try {
      await settingsAPI.killSessions(keepMe);
      if (!keepMe) {
        logout();
        return;
      }
      setMessage({ type: 'success', text: 'All other sessions revoked.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed.' });
    }
  };

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div className="settings-page-modern">
      <div className="settings-sidebar">
        <div className="settings-profile">
          <div className="profile-avatar">{user?.username?.[0]?.toUpperCase() || 'U'}</div>
          <div className="profile-info">
            <strong>{user?.username}</strong>
            <span>{user?.role}</span>
          </div>
        </div>
        <nav className="settings-tabs">
          {TABS.filter(t => !t.adminOnly || isAdmin()).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`tab-item ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon /> {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="settings-main">
        <div className="settings-header">
          <h2>{TABS.find(t => t.id === activeTab)?.label}</h2>
          {isAdmin() && (
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              <FiCheck /> {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        {message && (
          <div className={`settings-message ${message.type}`}>{message.text}</div>
        )}

        <div className="settings-content">
          {/* General & UI */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h3>Account</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" value={user?.username || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={user?.email || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <input type="text" value={user?.role || ''} disabled />
                </div>
              </div>

              {/* Agents never sign billing documents, so they don't store a signature. */}
              {!isAgent() && (
                <>
                  <h3>My signature</h3>
                  <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px' }}>
                    Used when you apply your signature to a quotation, invoice or receipt.
                    A PNG or JPG with a transparent or white background works best.
                  </p>
                  <SignatureUpload />
                </>
              )}

              <h3><FiSun /> Theme</h3>
              <div className="theme-toggle-modern">
                <button
                  className={(local.theme || theme) === 'light' ? 'active' : ''}
                  onClick={() => { updateLocal('theme', 'light'); toggleTheme('light'); }}
                >
                  <FiSun /> Light
                </button>
                <button
                  className={(local.theme || theme) === 'dark' ? 'active' : ''}
                  onClick={() => { updateLocal('theme', 'dark'); toggleTheme('dark'); }}
                >
                  <FiMoon /> Dark
                </button>
              </div>

              {isAdmin() && (
                <>
                  <h3><FiDollarSign /> Currency & Exchange</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Base Currency</label>
                      <select value={local.base_currency || 'USD'} onChange={(e) => updateLocal('base_currency', e.target.value)}>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="ZAR">ZAR</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>TRY Rate (1 USD = ? TRY)</label>
                      <input type="number" step="0.01" value={local.currency_try_rate ?? 34.5} onChange={(e) => updateLocal('currency_try_rate', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="form-group">
                      <label>EUR Rate (1 USD = ? EUR)</label>
                      <input type="number" step="0.01" value={local.currency_eur_rate ?? 0.92} onChange={(e) => updateLocal('currency_eur_rate', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="form-group">
                      <label>Buffer % (quotation protection)</label>
                      <input type="number" step="0.1" value={local.currency_buffer_pct ?? 2} onChange={(e) => updateLocal('currency_buffer_pct', parseFloat(e.target.value) || 0)} />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Security — admin only, see TABS */}
          {activeTab === 'security' && isAdmin() && (
            <div className="settings-section">
              <h3><FiLock /> Two-Factor Authentication</h3>
              <div className="form-group">
                <label>Require 2FA for</label>
                <select value={local.require_2fa || 'none'} onChange={(e) => updateLocal('require_2fa', e.target.value)} disabled={!isAdmin()}>
                  <option value="none">No one</option>
                  <option value="admins">Admins only</option>
                  <option value="all">All users</option>
                </select>
              </div>

              <h3><FiLogOut /> Session Kill Switch</h3>
              <p className="help">Force logout all users. Use if a device is lost or compromised.</p>
              <div className="action-buttons">
                <button className="btn-warning" onClick={() => handleKillSessions(true)} disabled={!isAdmin()}>
                  Revoke all other sessions
                </button>
                <button className="btn-danger" onClick={() => handleKillSessions(false)} disabled={!isAdmin()}>
                  Revoke ALL sessions (logout everyone)
                </button>
              </div>
            </div>
          )}

          {/* Notifications — admin only, see TABS */}
          {activeTab === 'notifications' && isAdmin() && (
            <div className="settings-section">
              <h3>Channel Preferences</h3>
              <div className="form-group">
                <label>New Lead</label>
                <select value={Array.isArray(local.new_lead_channels) ? local.new_lead_channels[0] : 'email'} onChange={(e) => updateLocal('new_lead_channels', [e.target.value])} disabled={!isAdmin()}>
                  <option value="email">Email only</option>
                  <option value="in_app">In-app only</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              <div className="form-group">
                <label>Visa Rejection (high priority)</label>
                <input
                  type="text"
                  value={Array.isArray(local.visa_rejection_channels) ? local.visa_rejection_channels.join(', ') : 'sms, whatsapp, email'}
                  onChange={(e) => updateLocal('visa_rejection_channels', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="sms, whatsapp, email"
                  disabled={!isAdmin()}
                />
              </div>

              <h3><FiClock /> Do Not Disturb</h3>
              <p className="help">Working hours — no alerts outside this window.</p>
              <div className="form-row">
                <div className="form-group">
                  <label>Start</label>
                  <input type="time" value={local.dnd_start || '09:00'} onChange={(e) => updateLocal('dnd_start', e.target.value)} disabled={!isAdmin()} />
                </div>
                <div className="form-group">
                  <label>End</label>
                  <input type="time" value={local.dnd_end || '18:00'} onChange={(e) => updateLocal('dnd_end', e.target.value)} disabled={!isAdmin()} />
                </div>
              </div>
            </div>
          )}

          {/* Workflow — admin only, see TABS */}
          {activeTab === 'workflow' && isAdmin() && (
            <div className="settings-section">
              <h3>SLA Timers</h3>
              <div className="form-group">
                <label>Alert if application in "Pending" for more than (days)</label>
                <input type="number" min="1" value={local.sla_pending_days ?? 5} onChange={(e) => updateLocal('sla_pending_days', parseInt(e.target.value, 10) || 5)} disabled={!isAdmin()} />
              </div>

              <h3>Data Retention (Compliance)</h3>
              <div className="form-group">
                <label>Auto-delete documents X months after Arrival</label>
                <input type="number" min="1" value={local.data_retention_months ?? 12} onChange={(e) => updateLocal('data_retention_months', parseInt(e.target.value, 10) || 12)} disabled={!isAdmin()} />
              </div>
            </div>
          )}

          {/* Integrations — admin only, see TABS */}
          {activeTab === 'integrations' && isAdmin() && (
            <div className="settings-section">
              <h3>Webhooks</h3>
              <div className="form-group">
                <label>Send data to URL when event happens</label>
                <input type="url" value={local.webhook_url || ''} onChange={(e) => updateLocal('webhook_url', e.target.value)} placeholder="https://..." disabled={!isAdmin()} />
              </div>

              <h3>WhatsApp Business API</h3>
              <div className="form-group">
                <label>API Key (Twilio/WhatsApp)</label>
                <input type="password" value={local.whatsapp_api_key || ''} onChange={(e) => updateLocal('whatsapp_api_key', e.target.value)} placeholder="••••••••" disabled={!isAdmin()} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
